import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";

const FREE_SHIPPING_THRESHOLD = 150;

export const CartDrawer = () => {
  const { cart, open, setOpen, updateItem, removeItem, checkout, busy } = useCart();
  const lines = cart?.lines || [];
  const subtotal = cart?.cost?.subtotalAmount;
  const subtotalNum = subtotal ? parseFloat(subtotal.amount) : 0;
  const progress = Math.min(subtotalNum / FREE_SHIPPING_THRESHOLD, 1);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-border bg-background"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-border px-6 py-5">
              <h2 className="font-display text-lg font-bold uppercase tracking-tight">
                Cart{" "}
                <span className="font-mono text-xs text-muted-foreground">
                  [{lines.length}]
                </span>
              </h2>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-drawer-close"
                aria-label="Close cart"
                className="p-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {lines.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8 text-center">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                  Your cart is empty
                </p>
                <p className="font-display text-2xl font-bold uppercase tracking-tight">
                  The trail is waiting.
                </p>
                <Link
                  to="/shop"
                  onClick={() => setOpen(false)}
                  data-testid="cart-empty-shop-link"
                  className="border border-foreground px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-foreground hover:text-background"
                >
                  Shop now
                </Link>
              </div>
            ) : (
              <>
                <div className="border-b border-border px-6 py-4">
                  <div className="mb-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <span>
                      {progress >= 1
                        ? "Free shipping unlocked"
                        : `${money({ amount: (FREE_SHIPPING_THRESHOLD - subtotalNum).toFixed(2), currencyCode: subtotal?.currencyCode })} away from free shipping`}
                    </span>
                    <span>{Math.round(progress * 100)}%</span>
                  </div>
                  <div className="h-[3px] w-full bg-secondary">
                    <motion.div
                      className="h-full bg-signal"
                      animate={{ width: `${progress * 100}%` }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6" data-lenis-prevent>
                  {lines.map((line) => {
                    const m = line.merchandise;
                    const img = m?.image?.url || m?.product?.featuredImage?.url;
                    return (
                      <div
                        key={line.id}
                        data-testid={`cart-line-${line.id.slice(-6)}`}
                        className="flex gap-4 border-b border-border py-5"
                      >
                        <div className="h-24 w-20 shrink-0 overflow-hidden bg-secondary">
                          {img && (
                            <img src={img} alt={m?.product?.title} className="h-full w-full object-cover" />
                          )}
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-display text-sm font-semibold uppercase tracking-wide">
                                {m?.product?.title}
                              </p>
                              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                                {m?.title}
                              </p>
                            </div>
                            <button
                              onClick={() => removeItem(line.id)}
                              data-testid={`cart-line-remove-${line.id.slice(-6)}`}
                              aria-label="Remove item"
                              className="p-1 text-muted-foreground transition-colors hover:text-signal"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="mt-auto flex items-center justify-between">
                            <div className="flex items-center border border-border">
                              <button
                                onClick={() =>
                                  line.quantity > 1
                                    ? updateItem(line.id, line.quantity - 1)
                                    : removeItem(line.id)
                                }
                                disabled={busy}
                                data-testid={`cart-line-decrease-${line.id.slice(-6)}`}
                                aria-label="Decrease quantity"
                                className="p-2 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-8 text-center font-mono text-xs">
                                {line.quantity}
                              </span>
                              <button
                                onClick={() => updateItem(line.id, line.quantity + 1)}
                                disabled={busy}
                                data-testid={`cart-line-increase-${line.id.slice(-6)}`}
                                aria-label="Increase quantity"
                                className="p-2 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                            <span className="font-mono text-sm">
                              {money(line.cost?.totalAmount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-border px-6 py-5">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      Subtotal
                    </span>
                    <span className="font-display text-xl font-bold" data-testid="cart-subtotal">
                      {money(subtotal)}
                    </span>
                  </div>
                  <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    Shipping & taxes calculated at checkout
                  </p>
                  <button
                    onClick={checkout}
                    data-testid="checkout-button"
                    className="group flex w-full items-center justify-center gap-2 bg-signal py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-signal/90"
                  >
                    Checkout
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <Link
                    to="/cart"
                    onClick={() => setOpen(false)}
                    data-testid="view-cart-link"
                    className="mt-3 block text-center font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    View full cart
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
